import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  Checkbox,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { strNomalize } from '../../../helpers';
import { TState } from '../../../redux/store';
import useForm, { TField } from '../../form/Form';
import Modal from '../../modal/Modal';
import { TInput } from '../../tabs/type';
import { addressInputskeysImport, addressInputs } from './ManageInputs';
import RSelect from '../../form/RSelect';
import { useGetSelect } from '../../../redux/actions';
import { useCallback, useEffect, useState } from 'react';
import { fetchMatchingPdf, postMatchingPdf } from '../../../api/tabsApi';
import { chunk } from 'lodash';

const signerFields = ['first_name', 'last_name', 'email', 'phone_number'];
const tradSignerFields = ['Prénom', 'Nom', 'Email', 'Téléphone'];

const Signers = ({
  fields,
  data,
  onChange,
}: {
  fields: any[];
  data: Record<string, any>;
  onChange: (val: any) => void;
}) => {
  const key = `${fields[0].props.field.name.split('-')[0]}-isFixed`;
  const [isFixed, setIsFixed] = useState(false);

  const handleChange = useCallback(() => {
    setIsFixed((f) => {
      onChange((fd: any) => ({
        ...fd,
        ...fields.reduce(
          (prev, curr) => ({ ...prev, [curr.props.field.name]: undefined }),
          {},
        ),
        [key]: !f,
      }));
      return !f;
    });
  }, [setIsFixed]);

  useEffect(() => {
    setIsFixed(!!data[key]);
  }, [data[key]]);

  return (
    <>
      <Card p={3} mb={3}>
        <Flex gap={2} alignItems='center' position='absolute' right={3} top={1}>
          <Text whiteSpace='nowrap'>Vous êtes la personne qui signe ?</Text>
          <Checkbox isChecked={isFixed} onChange={handleChange} />
        </Flex>
        {fields}
      </Card>
    </>
  );
};

const ManagePdfMatching = () => {
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const rowData = useSelector((state: TState) => state.rowData);

  const getSelect = useGetSelect();

  const [isFixed, setIsFixed] = useState<boolean[]>([]);

  const allInputs: TInput[] = dataInputs?.reduce(
    (prev, input) =>
      input.name_id === 'planning_type'
        ? prev
        : input.type !== 'address'
        ? [...prev, input]
        : [
            ...prev,
            ...addressInputskeysImport.map((key, i) => ({
              ...input,
              label: `Adresse ${input.label} ${addressInputs[i].toUpperCase()}`,
              name_id: `${input.name_id}_${key}`,
            })),
          ],
    [] as TInput[],
  );

  const filteredFields = rowData?.fields?.filter((f: any) => !f.hide) || [];
  const matchFields: TField[] = filteredFields.map(
    ({ name, type }: any, idx: number) => ({
      label: isFixed[idx] && type === 'PDFCheckBox' ? 'Doit être coché ?' : '',
      name: strNomalize(name),
      type: isFixed[idx]
        ? type === 'PDFCheckBox'
          ? 'checkbox'
          : 'text'
        : 'select',
      options: isFixed[idx]
        ? []
        : allInputs
            ?.filter((i) =>
              type === 'PDFCheckBox' ? /select|user|switch/.test(i.type) : true,
            )
            ?.map((input) => ({
              label: `${input.label}${
                strNomalize(input.name_id) !== strNomalize(input.label)
                  ? ` (${input.name_id})`
                  : ''
              } / ${input.type}${
                input.description ? ` / ${input.description}` : ''
              }`,
              value: input.name_id,
            })),
      placeholder: isFixed[idx] ? 'Choisir une valeur' : 'Choisir un champ',
      noFormat: isFixed[idx] && type === 'PDFCheckBox',
    }),
  );

  const {
    Form: MatchForm,
    formData,
    setFormData,
  } = useForm(matchFields, {
    noRowData: true,
  });

  const signerFieldsInputs = rowData?.fields
    .filter((f: any) => f.sign)
    .flatMap((signer: any) =>
      signerFields.map((name, idx) => ({
        label: `${signer.label} ${tradSignerFields[idx]}`,
        name: `${strNomalize(signer.label)}-${name}`,
        type: 'select',
        typeFn: (data: any) =>
          data[`${strNomalize(signer.label)}-isFixed`] ? 'text' : 'select',
        options: allInputs
          ?.filter((i) => /text|tel/.test(i.type))
          ?.map((input) => ({
            label: `${input.label}${
              strNomalize(input.name_id) !== strNomalize(input.label)
                ? ` (${input.name_id})`
                : ''
            } / ${input.type}${
              input.description ? ` / ${input.description}` : ''
            }`,
            value: input.name_id,
          })),
        placeholder: 'Choisir un champ',
      })),
    );

  const {
    Form: SignerMatchForm,
    formData: signerData,
    setFormData: setSignerFormData,
  } = useForm(signerFieldsInputs, {
    noRowData: true,
  });

  // reminder_settings: {interval_in_days: 2},
  // signers_allowed_to_decline: false

  const optionsFields = [
    {
      label: 'Validation par',
      name: 'signature_authentication_mode',
      type: 'select',
      options: [
        {
          label: 'sms',
          value: 'otp_sms',
        },
        {
          label: 'email',
          value: 'otp_email',
        },
        {
          label: 'pas de validation',
          value: 'no_otp',
        },
      ],
    },
    // {
    //   label: 'Envoyer un rappel tous les (10 rappels max)',
    //   name: 'interval_in_days',
    //   type: 'select',
    //   options: [
    //     {
    //       label: 'jour',
    //       value: 1,
    //     },
    //     {
    //       label: '2 jours',
    //       value: 2,
    //     },
    //     {
    //       label: '7 jours',
    //       value: 7,
    //     },
    //     {
    //       label: '14 jours',
    //       value: 14,
    //     },
    //   ],
    // },
    {
      label: "Delais d'expiration",
      name: 'expiration_date',
      type: 'select',
      options: [
        { label: '1 semaine', value: '1w' },
        { label: '2 semaines', value: '2w' },
        { label: '3 semaines', value: '3w' },
        { label: '1 mois', value: '1M' },
        { label: '2 mois', value: '2M' },
        { label: '3 mois', value: '3M' },
        { label: '4 mois', value: '4M' },
        { label: '5 mois', value: '5M' },
        { label: '6 mois', value: '6M' },
      ],
    },
    {
      label: 'Peux refuser la signature ?',
      name: 'signers_allowed_to_decline',
      type: 'switch',
    },
  ];

  const {
    Form: OptionsMatchForm,
    formData: optionsData,
    setFormData: setOptionsFormData,
  } = useForm(optionsFields, {
    noRowData: true,
  });

  useEffect(() => {
    const getMatchs = async () => {
      const resp = await fetchMatchingPdf(rowData.name);

      if (!resp.data) return;

      setFormData(resp.data?.fields || {});
      setSignerFormData(resp.data?.signer || {});
      setOptionsFormData(resp.data?.options || {});

      MatchForm.forEach((input, idx) => {
        const pdfKey = input?.props?.field?.name;
        const isFixedData = resp.data.fields[`${pdfKey}-isFixed`];

        setIsFixed((arr) => {
          const newArr = [...arr];
          newArr[idx] = isFixedData;
          return newArr;
        });
      });
    };

    getMatchs();
  }, [rowData.name]);

  const handleSubmit = async () => {
    await postMatchingPdf({
      filename: rowData.name,
      fields: formData,
      signer: signerData,
      options: optionsData,
    });
  };

  const renderPdfMatching = () =>
    MatchForm.map((input, idx) => {
      const type = filteredFields[idx]?.type;
      const isCheckbox = type === 'PDFCheckBox';
      const pdfKey = input?.props?.field?.name;
      const inputKey = formData[pdfKey];
      const inputVal = getSelect(inputKey);
      const inputType = inputVal?.type;

      return (
        <Card mb={3} p={3} bgColor='#f9fafb'>
          <Flex gap={3}>
            <Text flexGrow={1}>
              {filteredFields[idx]?.label || filteredFields[idx]?.name}
            </Text>
            <Flex gap={2} alignItems='center'>
              <Text whiteSpace='nowrap'>Valeur fixe</Text>
              <Checkbox
                isChecked={isFixed[idx]}
                onChange={(e) => {
                  setIsFixed((arr) => {
                    const newArr = [...arr];
                    newArr[idx] = e.target.checked;
                    return newArr;
                  });
                  setFormData((arr) => ({
                    ...arr,
                    [`${pdfKey}-isFixed`]: e.target.checked,
                  }));
                }}
              />
            </Flex>
          </Flex>
          <Box maxH={isCheckbox && isFixed[idx] ? '30px' : ''}>
            <Flex
              gap={3}
              alignItems='end'
              position='relative'
              top={isCheckbox && isFixed[idx] ? '-10px' : ''}
            >
              {input}
              {isCheckbox && !isFixed[idx] && (
                <RSelect
                  options={
                    inputType === 'switch'
                      ? [
                          { label: 'OUI', value: true },
                          { label: 'NON', value: false },
                        ]
                      : inputVal?.options
                  }
                  value={inputVal?.options?.find(
                    (opt) => opt.value === formData[`${pdfKey}-value`],
                  )}
                  onChange={(e) => {
                    setFormData((d) => ({
                      ...d,
                      [`${pdfKey}-value`]: e?.value,
                    }));
                  }}
                />
              )}
            </Flex>
          </Box>
        </Card>
      );
    });

  const forms = chunk(SignerMatchForm, 4);

  const renderPdfTabs = () => (
    <Tabs>
      <TabList>
        <Tab>Matching</Tab>
        <Tab>Signataire</Tab>
        <Tab>Options</Tab>
      </TabList>

      <TabPanels>
        <TabPanel px={0}>{renderPdfMatching()}</TabPanel>
        <TabPanel px={0}>
          {forms.map((f) => (
            <Signers
              fields={f}
              onChange={setSignerFormData}
              data={signerData}
            />
          ))}
        </TabPanel>
        <TabPanel px={0}>{OptionsMatchForm}</TabPanel>
      </TabPanels>
    </Tabs>
  );

  const modalOptions = {
    editPdfMatching: {
      component: renderPdfTabs(),
      action: handleSubmit,
      title: 'Matching des champs PDF',
      actionLabel: 'Appliquer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManagePdfMatching;
